import { Printer, ChevronLeft, Award, Loader2, BookOpen, Eye, FileText, FileDown, BookX, ListChecks } from 'lucide-react';
import { getStudentReportData, printPdfBlob, saveReportCardPdf } from './ReportCards';
import type { PracticeExam } from '../../types/practice-exam';
import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect } from 'react';
import { ApiService } from '../../services/api';
import { useModal } from '../../hooks/useModal';
import WebStudentReportCard from './components/WebStudentReportCard';
import ReportCardPdfPreview from './components/ReportCardPdfPreview';
import ExamSolutions from './components/ExamSolutions';

export default function MyReportCards() {
    const { user, systemConfig } = useAuth();
    const { showAlert } = useModal();
    const [loading, setLoading] = useState(false);
    const [exams, setExams] = useState<PracticeExam[]>([]);
    const [selectedExam, setSelectedExam] = useState<PracticeExam | null>(null);
    const [reportData, setReportData] = useState<any>(null);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [totalStudents, setTotalStudents] = useState(0);
    const [previewMode, setPreviewMode] = useState<'web' | 'a4'>('web');
    const [pdfLoading, setPdfLoading] = useState(false);
    const [printLoading, setPrintLoading] = useState(false);
    const [bookletLoading, setBookletLoading] = useState(false);
    const [solutionsOpen, setSolutionsOpen] = useState(false);

    useEffect(() => {
        fetchExams();
    }, []);

    const fetchExams = async () => {
        try {
            setLoading(true);
            const res = await ApiService.getUserPracticeExams({ limit: 100 });
            setExams(res.rows || []);
        } catch (error) {
            console.error(error);
            showAlert({ title: 'Hata', message: 'Denemeler yüklenirken hata oluştu', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleExamClick = async (exam: PracticeExam) => {
        if (!user) return;

        try {
            setSelectedExam(exam);
            setResultsLoading(true);
            setPreviewMode('web');

            const res = await ApiService.getMyPracticeExamResult(exam.id);
            const myResult = (res as any)?.result || null;
            const overallStats = (res as any)?.overallStats || {};
            const totalStudentsFromStats = Number(overallStats?.totalStudents) || 0;
            setTotalStudents(totalStudentsFromStats);

            if (myResult) {
                const examMetadata = {
                    bookletMapping: (res as any)?.bookletMapping || {},
                    testNames: (res as any)?.testNames || {},
                    testAverages: (res as any)?.testAverages || {},
                    overallStats,
                    levelType: exam.levelType,
                    yksMode: exam.yksMode,
                    systemType: exam.systemType || (exam.isInternal ? 'internal' : undefined),
                };

                const data = {
                    ...getStudentReportData(myResult, examMetadata),
                    examId: exam.id,
                    additionalInfo: (res as any)?.additionalInfo || null,
                };
                setReportData(data);
            } else {
                setReportData(null);
                showAlert({ title: 'Bilgi', message: 'Bu deneme için sonucunuz bulunamadı.', type: 'info' });
            };
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error.message || 'Sonuç yüklenirken hata oluştu', type: 'error' });
            setSelectedExam(null);
        } finally {
            setResultsLoading(false);
        }
    };

    const handleBack = () => {
        setSelectedExam(null);
        setReportData(null);
        setPreviewMode('web');
    };

    // Yazdır, indirilen detaylı karnenin aynısını basar (yönetici Karne ekranıyla tek şablon)
    const handlePrint = async () => {
        if (!selectedExam) return;
        try {
            setPrintLoading(true);
            const { blob } = await ApiService.downloadMyPracticeExamReportCard(selectedExam.id);
            printPdfBlob(blob);
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error.message || 'Karne yazdırma için hazırlanamadı', type: 'error' });
        } finally {
            setPrintLoading(false);
        }
    };

    const canDownloadErrorBooklet = selectedExam?.levelType === 'yks' && (selectedExam?.systemType === 'internal' || Boolean(selectedExam?.isInternal));

    const handleDownloadErrorBooklet = async () => {
        if (!selectedExam) return;
        try {
            setBookletLoading(true);
            saveReportCardPdf(await ApiService.downloadMyPracticeExamErrorBooklet(selectedExam.id));
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error.message || 'Hata kitapçığı oluşturulurken hata oluştu', type: 'error' });
        } finally {
            setBookletLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!selectedExam) return;
        try {
            setPdfLoading(true);
            saveReportCardPdf(await ApiService.downloadMyPracticeExamReportCard(selectedExam.id));
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error.message || 'Karne PDF\'i oluşturulurken hata oluştu', type: 'error' });
        } finally {
            setPdfLoading(false);
        }
    };

    if (solutionsOpen && selectedExam) {
        return <ExamSolutions exam={selectedExam} onBack={() => setSolutionsOpen(false)} />;
    }

    return (
        <div className="flex h-full flex-col space-y-4 p-4 sm:space-y-6 sm:p-6 overflow-y-auto">
            <div className="no-print flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3 sm:items-center sm:gap-4">
                    {selectedExam && (
                        <button
                            onClick={handleBack}
                            className="rounded-full p-2 text-gray-600 transition-colors hover:bg-gray-100"
                        >
                            <ChevronLeft size={22} />
                        </button>
                    )}
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold leading-tight text-gray-800 sm:text-2xl">
                            {selectedExam ? selectedExam.name : 'Karnelerim'}
                        </h2>
                        <p className="mt-1 text-sm leading-6 text-gray-500 sm:text-base">
                            {selectedExam ? 'Sınav Sonuç Karnesi ve Detaylı Analiz' : 'Girdiğiniz sınavların karnelerini buradan görüntüleyebilirsiniz.'}
                        </p>
                    </div>
                </div>
                {selectedExam && reportData && (
                    <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-nowrap">
                        <button
                            onClick={() => setPreviewMode(previewMode === 'web' ? 'a4' : 'web')}
                            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition-all sm:flex-initial sm:px-5 ${previewMode === 'a4'
                                    ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                }`}
                            title={previewMode === 'a4' ? 'Sistem görünümüne dön' : 'A4 çıktı önizlemesi'}
                        >
                            {previewMode === 'a4' ? <FileText size={16} /> : <Eye size={16} />}
                            <span>{previewMode === 'a4' ? 'Web Görünümü' : 'A4 Önizleme'}</span>
                        </button>
                        <button
                            onClick={handleDownloadPdf}
                            disabled={pdfLoading}
                            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-indigo-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-60 sm:flex-initial sm:px-5"
                            title="Detaylı karne analizini PDF olarak indir"
                        >
                            {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                            <span className="sm:hidden">Karne PDF</span>
                            <span className="hidden sm:inline">Detaylı Karne PDF</span>
                        </button>
                        {selectedExam.hasSolutions && (
                            <button
                                onClick={() => setSolutionsOpen(true)}
                                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-medium text-gray-700 border border-gray-300 transition-all hover:bg-gray-50 sm:flex-initial sm:px-5"
                                title="Soruları, cevabını ve yazılı çözümleri gör"
                            >
                                <ListChecks size={16} />
                                <span>Soru Çözümleri</span>
                            </button>
                        )}
                        {canDownloadErrorBooklet && (
                            <button
                                onClick={handleDownloadErrorBooklet}
                                disabled={bookletLoading}
                                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-rose-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-60 sm:flex-initial sm:px-5"
                                title="Denemede kaçırdığın kazanımlardan çalışma kitapçığı (PDF)"
                            >
                                {bookletLoading ? <Loader2 size={16} className="animate-spin" /> : <BookX size={16} />}
                                <span>Hata Kitapçığı</span>
                            </button>
                        )}
                        <button
                            onClick={handlePrint}
                            disabled={printLoading}
                            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-60 sm:flex-initial sm:px-5"
                        >
                            {printLoading ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                            <span>Yazdır / İndir</span>
                        </button>
                    </div>
                )}
            </div>

            {!selectedExam ? (
                loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                ) : exams.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-12 text-center border-2 border-dashed border-gray-200">
                        <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900">Henüz Sınav Kaydı Yok</h3>
                        <p className="text-gray-500 mt-1">Sistemde açıklanmış sınav sonucu bulunmamaktadır.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {exams.map((exam) => (
                            <div
                                key={exam.id}
                                onClick={() => handleExamClick(exam)}
                                className="group bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <Award className="w-16 h-16 text-blue-600 transform rotate-12" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800 line-clamp-2" title={exam.name}>
                                    {exam.name}
                                </h3>
                                <p className="text-sm text-gray-500 mb-4 flex items-center gap-2">
                                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                                        {new Date(exam.date || '').toLocaleDateString('tr-TR')}
                                    </span>
                                </p>
                                <div className="flex items-center text-sm font-medium text-blue-600 group-hover:translate-x-1 transition-transform">
                                    Karneyi Görüntüle
                                    <ChevronLeft className="w-4 rotate-180 ml-1" />
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : resultsLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            ) : reportData ? (
                <>
                    {previewMode === 'web' ? (
                        <WebStudentReportCard
                            data={reportData}
                            examName={selectedExam?.name}
                            companyLogo={systemConfig?.companyLogo || undefined}
                            companyName={systemConfig?.companyName || undefined}
                            totalStudents={totalStudents}
                        />
                    ) : selectedExam ? (
                        <ReportCardPdfPreview
                            cacheKey={selectedExam.id}
                            load={() => ApiService.downloadMyPracticeExamReportCard(selectedExam.id)}
                        />
                    ) : null}
                </>
            ) : null}

        </div>
    );
}
